import { color as createColorResult } from '@uiw/color-convert';
import {
  rgbaToHsva, rgbStringToHsva, validHex,
} from '@uiw/react-color';
import memoizee from 'memoizee';
import type { HexColor } from '~/_shared/utils/colors/colors.types';
import { memoizeWeak } from '~/_shared/utils/memoize/memoize';
import { type StorageService } from '../../utils/storageService';
import { isString } from '../../utils/typeGuards';
import {
  type ColorInput, type ColorResult, type HsvaColor, type RgbaColor,
} from './colorPicker.types';

export const isValidHex = (hex: string): boolean => {
  return validHex(hex);
};

export const isRgbaColor = (color: ColorInput): color is RgbaColor => {
  if (typeof color === 'string') {
    return false;
  }

  return 'r' in color && 'g' in color && 'b' in color && 'a' in color;
};

export const isHsvaColor = (color: ColorInput): color is HsvaColor => {
  if (typeof color === 'string') {
    return false;
  }

  return 'h' in color && 's' in color && 'v' in color;
};

const createColorInternal = (color: ColorInput): ColorResult => {
  if ((typeof color === 'string' && validHex(color)) || isHsvaColor(color)) {
    return createColorResult(color) as ColorResult;
  }
  else if (typeof color === 'string') {
    // rgb or rgba string type
    return createColorResult(rgbStringToHsva(color)) as ColorResult;
  }
  else if (isRgbaColor(color)) {
    return createColorResult(rgbaToHsva(color)) as ColorResult;
  }
  else {
    return color;
  }
};

type RemoveString<T> = Exclude<T, string>;
const createColorNoString = (color: RemoveString<ColorInput>) => createColorInternal(color);
const createColorNoStringMemoized = memoizeWeak(createColorNoString);

const createColorFromString = (color: string) => createColorInternal(color);
const createColorFromStringMemoized = memoizee(createColorFromString, { primitive: true });

export const createColor = (color: ColorInput): ColorResult => {
  if (isString(color)) {
    return createColorFromStringMemoized(color);
  }

  return createColorNoStringMemoized(color);
};

export const removeHash = (hex: string) => hex.replace('#', '');
export const guaranteeHash = (hex: string): HexColor => hex.startsWith('#') ? hex as HexColor : `#${hex}`;

export const hexColorWithOpacityToString = (color: string, opacity: number) => {
  const newColor = createColorWithOpacity(color, opacity);
  return rgbColorToString({ ...newColor.rgb, a: opacity });
};

export const rgbColorToString = memoizeWeak((color: RgbaColor): string => {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a ?? 1})`;
});

export const hexAndOpacityToRGBA = memoizee((hex: string, opacity: number): RgbaColor => {
  const rgbColor = createColorResult(hex).rgb;

  return { ...rgbColor, a: opacity ?? 1 };
});

export const createColorWithOpacity = (hex: string, opacity: number): ColorResult => {
  return createColor(hexAndOpacityToRGBA(hex, opacity));
};

const PRESET_COLORS_STORAGE_KEY = 'preset-colors-color-picker';
export const getPresetColors = (storageService: StorageService) => {
  const storedState = storageService.getLocalStorageItem(PRESET_COLORS_STORAGE_KEY);

  if (storedState) {
    const colors: string[] = JSON.parse(storedState);

    return colors;
  }

  return [];
};

const MAX_PRESET_COLORS = 8;
export const getMaxAmountOfColors = (colors: readonly string[], count?: number) =>
  colors.slice(0, count || MAX_PRESET_COLORS);

export const storePresetColor = (storageService: StorageService, hex: string) => {
  const presetColors = getPresetColors(storageService).filter(c => c !== hex);

  presetColors.unshift(hex);

  storageService.setLocalStorageItem(PRESET_COLORS_STORAGE_KEY, JSON.stringify(getMaxAmountOfColors(presetColors)));
};
